import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from "recharts";
import { FaFireAlt, FaRunning, FaUtensils } from "react-icons/fa";

const caloriesData = {
  consumed: 1860,
  burned: 600,
  remaining: 500,
  macros: [
    { name: "Protein", value: 90, color: "#34D399" },
    { name: "Carbs", value: 180, color: "#60A5FA" },
    { name: "Fats", value: 70, color: "#F87171" },
  ],
};

export default function FeatureCalorieTracking() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="min-h-screen bg-gradient-to-br from-[#0b1410] via-[#0e1a10] to-[#0b1410] text-white px-6 py-20 font-manrope relative overflow-hidden">
      <div className="max-w-6xl mx-auto z-10 relative">
        {/* Header */}
        <motion.h1
          className="text-4xl sm:text-5xl font-extrabold text-green-400 mb-10 text-center drop-shadow-lg"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {isLoading ? (
            <div className="h-10 bg-gray-700 rounded w-2/3 mx-auto animate-pulse" />
          ) : (
            "Comprehensive Calorie Tracking"
          )}
        </motion.h1>

        <motion.p
          className="text-lg text-gray-300 text-center max-w-3xl mx-auto mb-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          {isLoading ? (
            <div className="h-4 bg-gray-700 rounded w-4/5 mx-auto animate-pulse" />
          ) : (
            "Visualize your daily energy metrics like never before. FitTrack transforms your nutrition into beautiful interactive data, empowering smarter fitness decisions."
          )}
        </motion.p>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 relative z-10">
          {isLoading
            ? Array(3)
                .fill(0)
                .map((_, i) => (
                  <div
                    key={i}
                    className="bg-white/10 rounded-xl h-40 animate-pulse"
                  ></div>
                ))
            : [
                {
                  title: "Calories Consumed",
                  value: caloriesData.consumed,
                  color: "green",
                  note: "Tracked from daily meals",
                  icon: <FaUtensils className="text-green-300 text-2xl" />,
                },
                {
                  title: "Calories Burned",
                  value: caloriesData.burned,
                  color: "blue",
                  note: "Steps, activities & workouts",
                  icon: <FaRunning className="text-blue-300 text-2xl" />,
                },
                {
                  title: "Remaining to Burn",
                  value: caloriesData.remaining,
                  color: "red",
                  note: "Left to reach your daily goal",
                  icon: <FaFireAlt className="text-red-300 text-2xl" />,
                },
              ].map((card, i) => (
                <motion.div
                  key={i}
                  className={`glass-card border-t-4 border-${card.color}-400 p-6 rounded-xl shadow-xl backdrop-blur-md bg-white/5 hover:scale-105 transition-transform duration-500`}
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: i * 0.2 }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    {card.icon}
                    <h3 className={`text-xl font-bold text-${card.color}-300`}>
                      {card.title}
                    </h3>
                  </div>
                  <p className="text-4xl font-extrabold text-white">
                    {card.value} kcal
                  </p>
                  <p className="text-sm text-gray-400 mt-2">{card.note}</p>
                </motion.div>
              ))}
        </div>

       {/* Chart */}
<div className="mt-20 max-w-xl mx-auto z-10 relative">
  <h3 className="text-2xl font-semibold text-center text-white mb-6">
    {isLoading ? (
      <div className="h-6 w-3/4 mx-auto bg-gray-700 rounded animate-pulse" />
    ) : (
      "🧬 Macro Nutrient Breakdown"
    )}
  </h3>

  <div className="bg-white/5 backdrop-blur-sm p-6 rounded-2xl shadow-inner border border-gray-700 min-h-[350px]">
    {isLoading ? (
      <div className="h-64 w-full bg-gray-700 animate-pulse rounded-xl" />
    ) : (
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={caloriesData.macros}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            fill="#8884d8"
            dataKey="value"
            label={({ name }) => name}
            labelLine={false}
          >
            {caloriesData.macros.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              // backgroundColor: "#1f2937",
              border: "none",
              borderRadius: "10px",
              color: "white",
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    )}

    {!isLoading && (
      <div className="mt-6 flex justify-center gap-6 text-sm">
        {caloriesData.macros.map((macro) => (
          <div key={macro.name} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: macro.color }}
            ></div>
            <span className="text-gray-300">{macro.name}</span>
          </div>
        ))}
      </div>
    )}
  </div>
</div>

      </div>

      {/* Background Bubbles */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 left-10 w-32 h-32 bg-green-300 rounded-full blur-2xl opacity-10 animate-ping"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-blue-300 rounded-full blur-2xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-40 left-[45%] w-24 h-24 bg-red-400 rounded-full blur-xl opacity-20 animate-bounce"></div>
      </div>
    </section>
  );
}
